<template>
  <div
    :id="idLabel"
    class="openGallery"
    @click="emitCloseEvent(false)"
  >
    <div class="openGallery__nav openGallery__nav--left">
      <button
        class="openGallery__nav__button center"
        @click.stop="nextImage(-1)"
      >
        <ArrowIcon direction="left" />
      </button>
    </div>

    <div class="openGallery__info">
      <button
        class="openGallery__info__back"
        @click="emitCloseEvent(false)"
      >
        <ArrowIcon direction="left" />
      </button>
      <span class="openGallery__info__counter">{{ currentImgIndex + 1 }} / {{ totalImages }}</span>
    </div>

    <div class="openGallery__current-image">
      <img
        :src="ShowImage(currentImage)"
        alt=""
        class="openGallery__current-image__image"
      >
    </div>

    <div class="openGallery__secondary-images">
      <img
        v-for="(image, index) in secondaryImages"
        :key="image.uuid"
        :src="ShowImage(image)"
        alt=""
        :class="[
          'openGallery__secondary-images__img',
          {
            'openGallery__secondary-images__img--current':
              index === currentImgIndex,
          },
        ]"
        @click.stop="setImage(image.uuid)"
      >
    </div>

    <div class="openGallery__nav openGallery__nav--right">
      <button
        class="openGallery__nav__button center"
        @click.stop="nextImage(+1)"
      >
        <ArrowIcon direction="right" />
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { useMobileDevice } from '@/core/composable/shared/useMobileDevice'
import { useSwiper } from '@/core/composable/shared/useSwipper'

import ArrowIcon from '@/core/components/shared/icons/Arrow.icon.vue'
import type { Picture } from '~/core/ts/entity/Picture'

function generateStringID() {
  return Math.random().toString(36).substring(2, 9)
}

export default defineNuxtComponent({
  components: {
    ArrowIcon,
  },
  props: {
    images: {
      type: Array as PropType<Array<Picture>>,
      required: true,
    },
    id: {
      type: String,
      default: () => generateStringID(),
    },
  },
  setup(props, { emit }) {
    const secondaryImagesStart = 0
    const secondaryImagesAmount = 5
    const secondaryImages = computed(() : Array<Picture> => {
      return props.images.slice(secondaryImagesStart, secondaryImagesAmount)
    })

    const currentImgIndex = useState('currentImgIndex', () => 0)
    const currentImage = computed(() => props.images[currentImgIndex.value])

    const nextImage = (direction: number) => {
      let newIndex = currentImgIndex.value + direction
      const lastIndex = props.images.length - 1

      if (newIndex > lastIndex) {
        newIndex = 0
      }

      if (newIndex < 0) {
        newIndex = lastIndex
      }

      currentImgIndex.value = newIndex
    }

    const totalImages = computed(() => props.images.length)

    const idLabel = computed(() => {
      return `open-gallery-${props.id}`
    })

    useSwiper(idLabel.value, {
      handleSwipeLeft: () => nextImage(-1),
      handleSwipeRight: () => nextImage(1),
    })

    const setImage = (uuid: string) => {
      currentImgIndex.value = props.images.findIndex((image: Picture) => {
        return image.uuid === uuid
      })
    }

    const emitCloseEvent = (checkScreen = true) => {
      if (!checkScreen || !isMobile.value) {
        emit("close")
      }
    }

    const { isMobile } = useMobileDevice()

    const defaultImage = 'https://marketplace-assets.s3.eu-west-3.amazonaws.com/2020/10/45656-pre-barrosapalace-spa4206.jpg'
    const ShowImage = (image?: Picture, defaultImageSrc: string = defaultImage): string => {
      if (!image) {
        return defaultImageSrc
      }

      return image.url
    }

    return {
      idLabel,
      isMobile,
      ShowImage,
      currentImage,
      secondaryImages,
      nextImage,
      setImage,
      emitCloseEvent,
      currentImgIndex,
      totalImages,
    }
  },
})
</script>

<style lang="scss" scoped>
.openGallery {
  @apply bg-gray-900;
  @apply w-full;
  @apply h-full;
  @apply fixed;
  @apply top-0;
  @apply left-0;
  @apply z-50;

  display: grid;
  grid-template-areas:
    "info"
    "currentImage";
  grid-template-rows: auto 1fr;

  @screen lg {
    grid-template-areas:
      "currentImage"
      "secondaryImages";
    grid-template-rows: 1fr calc(135px + 70px);
  }

  &__info {
    grid-area: info;
    @apply grid;
    @apply grid-cols-3;
    @apply text-white;
    padding: 40px 25px;

    &__back {
      @apply text-left;
    }
    &__counter {
      @apply text-base;
      @apply font-bold;
      @apply text-center;
    }

    @screen lg {
      @apply hidden;
    }
  }

  &__current-image {
    grid-area: currentImage;
    display: flex;
    align-items: center;
    justify-content: center;

    @screen lg {
      margin-top: 30px;
    }
    &__image {
      max-height: 60vh;
    }
  }

  &__secondary-images {
    grid-area: secondaryImages;
    @apply hidden;

    @screen lg {
      display: grid;
      grid-template-columns: repeat(5, 195px);
      grid-template-rows: 135px;
      gap: 10px;
      margin: auto;
      margin-bottom: 30px;
    }

    &__img {
      width: 100%;
      height: 100%;
      @apply cursor-pointer;
      @apply relative;
      filter: grayscale(100%);
      transition: 0.3s;
      object-fit: cover;
      &--current,
      &:hover {
        filter: grayscale(0%);
      }
    }
  }

  &__nav {
    @apply absolute;
    top: 55%;

    &--left {
      left: 15px;
      @screen lg {
        left: 95px;
      }
    }

    &--right {
      right: 15px;
      @screen lg {
        right: 95px;
      }
    }

    @screen lg {
      top: 40%;
    }

    &__button {
      @apply bg-gray-700;
      @apply opacity-75;
      @apply rounded-full;
      @apply flex;
      @apply justify-center;
      @apply items-center;
      @apply h-11;
      @apply w-11;
    }
  }
}
</style>
